import React, { useEffect, useRef, useState } from "react";
import { FreteGratisMainContainer } from "../cuponsMain/styles";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import Form from "../../Form";
import { BotoesContainer, Titulo } from "../../../styled/Styled";
import { Button } from "../../Button";
import Modal from "../../Modal/Modal";
import Brinde from "../Brinde/Brinde";
import BrindesListagem from "../BrindesListagem/BrindesListagem";
import Swal from "sweetalert2";
import moment from "moment";
import { brindesApi } from "../../../hooks/brindesApi";
import { Brinde as BrindeType } from "../../../types/Brinde";

const brindeVazio: BrindeType = {
  nome: "",
  tipo_regra: "valor",
  valor_minimo: null,
  quantidade_minima: null,
  estoque: 0,
  peso: 0,
  ativo: true,
  data_expiracao: null,
};

const BrindesMain: React.FC = () => {
  const { theme } = useTheme();
  const api = brindesApi();

  const [brindes, setBrindes] = useState<BrindeType[]>([]);
  const [modalBrinde, setModalBrinde] = useState(false);
  const [brinde, setBrinde] = useState<BrindeType>({ ...brindeVazio });
  const [formValid, setFormValid] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const editandoId = useRef<number | undefined>(undefined);

  const carregar = async () => {
    try {
      const res = await api.listar();
      setBrindes(res.data || []);
    } catch (e) {
      setBrindes([]);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const handleAdicionar = () => {
    editandoId.current = undefined;
    setBrinde({ ...brindeVazio });
    setModalBrinde(true);
  };

  const handleEditar = (index: number) => {
    const b = brindes[index];
    editandoId.current = b.id;
    setBrinde({ ...b, imagem_base64: undefined });
    setModalBrinde(true);
  };

  const handleToggle = async (index: number, ativo: boolean) => {
    const b = brindes[index];
    if (!b.id) return;
    const copia = [...brindes];
    copia[index] = { ...b, ativo };
    setBrindes(copia);
    try {
      await api.ativar(b.id, ativo);
    } catch (e) {
      carregar();
    }
  };

  const validar = (): boolean => {
    if (!brinde.nome || brinde.nome.trim() === "") {
      Swal.fire({ icon: "warning", title: "Campo obrigatório", text: "Nome do brinde é obrigatório." });
      return false;
    }
    // Imagem obrigatória: nova (base64) ou existente (na edição).
    if (!brinde.imagem_base64 && !brinde.imagem_externa_2 && !brinde.imagem_externa && !brinde.foto) {
      Swal.fire({ icon: "warning", title: "Imagem obrigatória", text: "Adicione uma imagem ao brinde." });
      return false;
    }
    if (brinde.tipo_regra === "valor" && (!brinde.valor_minimo || brinde.valor_minimo <= 0)) {
      Swal.fire({ icon: "warning", title: "Valor mínimo inválido", text: "Informe o valor mínimo da sacola." });
      return false;
    }
    if (brinde.tipo_regra === "quantidade" && (!brinde.quantidade_minima || brinde.quantidade_minima <= 0)) {
      Swal.fire({ icon: "warning", title: "Quantidade inválida", text: "Informe a quantidade mínima de produtos." });
      return false;
    }
    // Regra não pode repetir: mesmo tipo + mesmo valor de outro brinde da empresa.
    const valorRegraAtual =
      brinde.tipo_regra === "valor" ? brinde.valor_minimo : brinde.quantidade_minima;
    const regraDuplicada = brindes.some(
      (b) =>
        b.id !== editandoId.current &&
        b.tipo_regra === brinde.tipo_regra &&
        (b.tipo_regra === "valor" ? b.valor_minimo : b.quantidade_minima) === valorRegraAtual
    );
    if (regraDuplicada) {
      Swal.fire({
        icon: "warning",
        title: "Regra duplicada",
        text:
          brinde.tipo_regra === "valor"
            ? "Já existe um brinde com esse valor mínimo de sacola. Use um valor diferente."
            : "Já existe um brinde com essa quantidade mínima de produtos. Use uma quantidade diferente.",
      });
      return false;
    }
    if (
      brinde.data_expiracao &&
      (!moment(brinde.data_expiracao).isValid() || moment(brinde.data_expiracao).isBefore(moment()))
    ) {
      Swal.fire({ icon: "warning", title: "Data inválida", text: "Se preenchida, a data de expiração deve ser válida e futura." });
      return false;
    }
    return true;
  };

  const salvar = async () => {
    if (salvando) return;
    if (!validar()) return;
    setSalvando(true);
    try {
      await api.salvar({ ...brinde, id: editandoId.current });
      setModalBrinde(false);
      await carregar();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Erro", text: "Não foi possível salvar o brinde." });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={modalBrinde}
        setModal={setModalBrinde}
        width={"100rem"}
        maxWidth="98vw"
        backdrop={"static"}
        padding="3rem 4rem"
        onUseEffect={() => {
          setFormValid(false);
        }}
      >
        <Form
          id="brinde"
          onSubmit={(e) => {
            e.preventDefault();
            salvar();
          }}
          setFormValid={setFormValid}
        >
          <Titulo theme={theme} className="fonteTitulo">
            {editandoId.current === undefined ? "Adicionar" : "Editar"} Brinde
          </Titulo>
          <Brinde setBrinde={setBrinde} brinde={brinde} />
          <BotoesContainer theme={theme}>
            <div className="cancelar fonteTexto" onClick={() => setModalBrinde(false)}>
              Cancelar
            </div>
            <Button
              title={salvando ? "Salvando..." : "Adicionar"}
              type="submit"
              className={`fonteTexto ${(!formValid || salvando) && "disabled"}`}
            />
          </BotoesContainer>
        </Form>
      </Modal>
      <FreteGratisMainContainer theme={theme}>
        <div className="dropdown">
          <BrindesListagem
            brindes={brindes}
            onEditarBrinde={handleEditar}
            onNovoBrinde={handleAdicionar}
            onToggleBrinde={handleToggle}
            mostrarTexto={true}
          />
        </div>
      </FreteGratisMainContainer>
    </>
  );
};

export default BrindesMain;
